import { addUrlParams, addVisSom } from "../Component/url"
import { IDataRequester, IDataRequesterHandler, IDataRequestResult, DataRequesterCacheEntryState } from "../domene"
import { Moment, utc } from "moment/moment"
import { getUrlParam } from "../Component/url"

import Models = Gf.Web.Models;

class DataRequesterCacheEntry {

    private state = DataRequesterCacheEntryState.Invalid;
    private value: any;
    private error: any;
    private handlers: IDataRequesterHandler[] = [];
    private expires?: Moment;

    constructor(
        private readonly resourceUrl: string,
        private readonly requester: (consumer: (value: any, error: any) => void) => void,
        private readonly cacheDurationMs: number | undefined) { }

    addHandler(handler: IDataRequesterHandler) {
        this.handlers.push(handler);
        if (this.isFetched() && !this.isExpired()) {
            handler.handleDataResponse(this.value, this.error, this.resourceUrl);
        } else {
            this.requestValue();
        }
    }

    private isExpired() {
        return (this.expires !== undefined) && (this.expires.isBefore(utc()));
    }

    private isFetched(): boolean {
        return this.state === DataRequesterCacheEntryState.Fetched;
    }

    getIfFetched<T>(): IDataRequestResult<T> | undefined {
        return this.isFetched() ? {
            value: this.value || null,
            error: this.error || null
        } : undefined;
    }

    removeHandler(handler: IDataRequesterHandler) {
        const i = this.handlers.indexOf(handler);       // Searches by reference
        this.handlers.splice(i, 1);
    }

    private requestValue() {
        if (this.state !== DataRequesterCacheEntryState.Fetching) {
            this.state = DataRequesterCacheEntryState.Fetching;
            this.requester((value: any, error: any) => {
                this.setValue(value, error);
            });
        }
    }

    setValue(value: any, error: any) {
        this.state = DataRequesterCacheEntryState.Fetched;
        this.expires = (this.cacheDurationMs === undefined) ? undefined : utc().add(this.cacheDurationMs, "milliseconds");
        this.value = value;
        this.error = error;
        // Note that the slice() is important, otherwise the handlers cannot remove themselves without causing bugs
        this.handlers.slice().forEach((handler) => {
            handler.handleDataResponse(this.value, this.error, this.resourceUrl);
        });
    }

    invalidate() {
        this.state = DataRequesterCacheEntryState.Invalid;
        this.value = undefined;
        if(this.handlers.length > 0) {
            this.requestValue();
        }
    }
}

export class DataRequesterCache {

    private cache: { [key: string]: DataRequesterCacheEntry } = {};

    getIfCached<T>(key: string): IDataRequestResult<T> | undefined {
        const entry = this.cache[key];
        return entry ? entry.getIfFetched<T>() : undefined;
    }

    addHandler(key: string, handler: IDataRequesterHandler, cacheDurationMs?: number) {
        const entry = this.ensureEntry(key, cacheDurationMs);
        entry.addHandler(handler);
    }

    removeHandler(key: string, handler: IDataRequesterHandler) {
        const entry = this.cache[key];
        if (entry) {
            entry.removeHandler(handler);
        }
    }

    invalidate(key: string) {
        const shouldInvalidate = (k: string) => {
            if(key === "/") {
                return true;
            }
            const i = k.indexOf("?");
            const start = (i === -1) ? k : k.substr(0, i);
            return start === key;
        }
        Object.keys(this.cache).slice().forEach(k => {
            if(shouldInvalidate(k)) {
                const entry = this.cache[k];
                if (entry) {
                    entry.invalidate();
                }
            }
        });
    }

    private put(key: string, value: any, error: any): DataRequesterCacheEntry {
        const entry = this.ensureEntry(key);
        entry.setValue(value, error);
        return entry;
    }

    private ensureEntry(key: string, cacheDurationMs?: number) {
        let entry = this.cache[key];
        if (entry == null) {
            entry = new DataRequesterCacheEntry(key, (consumer: (response: any, error: any) => void) => appContext.get(key, consumer), cacheDurationMs);
            this.cache[key] = entry;
        }
        return entry;
    };

    importWithStrings(other: Object) {
        Object.keys(other).forEach((key) => {
            const value = (other as any)[key];
            //const withDatesParsed = JSON.parse(JSON.stringify(value));
            this.put(key, value, null); // withDatesParsed); //JSON.parse(text));
        });
    }

    clear() {
        this.cache = {};
    }
}

export interface IDialogShower {
    showModal(popup: any): void;
    showErrorPopup(message: String[]): void;
    showDialog(message: string, action: () => void): void;
}

export class AppContext {

    contextPath = "";
    server: boolean;
    requester: IDataRequester;
    cache = new DataRequesterCache();
    redigerbar = true;
    visning = "";
    refresh: () => void;
    dialogShower: IDialogShower;
    innloggetBruker?: string;
    effektivAktørEpost?: string;
    effektivAktørNavn?: string;
    effektivAktørRelasjon?: Gf.Dom.Type.AktørrelasjonType;
    visSomAktorId?: number;
    erTffAnsatt: boolean;
    getCurrentLocation: () => string;
    log: (message: string) => void;
    getUrlParam(param: string): string | undefined {
        return getUrlParam(this.getCurrentLocation(), param);
    };
    getUrlParamInt(param: string): number | undefined {
        const value = this.getUrlParam(param);
        return value ? +value : undefined;
    };
    getQueryUrlParam(): string { return this.getUrlParam("q") || ""; }

    anonym(): boolean { return !this.effektivAktørRelasjon; }
    harRelasjon = (...relasjonType: Gf.Dom.Type.AktørrelasjonType[]) => !!this.effektivAktørRelasjon && relasjonType.indexOf(this.effektivAktørRelasjon) !== -1;

    addContextPathAndFixUrl = (url: string, fixUrl: boolean = true) => {
        const withContextPath = this.addContextPath(url);
        return fixUrl ? addUrlParams(withContextPath) : withContextPath;
    };
    setTimeout: (handler: () => any, timeout: number) => any;

    private addContextPath(url: string) { return this.contextPath + url; }

    /**
      * Bind something to the given resource, changing it as
      * @param url
      * @param handler
      */
    getIfCached<T>(url: string): IDataRequestResult<T> | undefined {
        return this.cache.getIfCached<T>(url);
    }

    /**
     * Bind something to the given resource, changing it as
     * @param url
     * @param handler
     */
    addHandler(url: string, handler: IDataRequesterHandler, cacheDurationMs?: number) {
        this.cache.addHandler(url, handler, cacheDurationMs);
    }

    removeHandler(url: string, handler: IDataRequesterHandler) {
        this.cache.removeHandler(url, handler);
    }

    invalidate(url: string) {
        this.cache.invalidate(url);
    }

    post(url: string, request: any, consumer: (response: any, error: any) => void) {
        this.requester.post(addVisSom(url), request, consumer);
    }

    get(url: string, consumer: (response: any, error: any) => void) {
        this.requester.get(url, consumer);
    }

    clearCache() { this.cache.clear(); }

    testServer: boolean;
    plassering: string;
}

export const appContext = new AppContext();


// WEBPACK FOOTER //
// ./Scripts/React/App/appContext.tsx